import { z } from 'zod';

/**
 * Geld-Typ laut CLAUDE.md §6. NIEMALS `number` für Beträge verwenden.
 * `amount` ist in Mikro-Einheiten gespeichert: 1 EUR = 1_000_000n.
 * Damit ist Float-Arithmetik (und die daraus folgenden Buchungsfehler) ausgeschlossen.
 */
export type Currency = 'EUR' | 'USD' | 'CHF';

export type Money = {
  amount: bigint;
  currency: Currency;
};

/** Mikro-Einheiten je Haupteinheit (1 EUR = 1.000.000 Mikro-EUR). */
export const MICRO_UNITS_PER_MAJOR = 1_000_000n;

/**
 * Erzeugt einen Money-Wert aus Haupt- und Nebeneinheit (z. B. 12 EUR 50 ct).
 * `minor` wird in Hundertstel der Haupteinheit interpretiert (Cent).
 */
export function money(major: bigint, minor: bigint, currency: Currency): Money {
  if (minor < 0n || minor >= 100n) {
    throw new RangeError(`minor muss zwischen 0 und 99 liegen, war ${minor}`);
  }
  const sign = major < 0n ? -1n : 1n;
  const absMajor = major < 0n ? -major : major;
  const amount = sign * (absMajor * MICRO_UNITS_PER_MAJOR + minor * 10_000n);
  return { amount, currency };
}

/** Addiert zwei Money-Werte gleicher Währung. */
export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount + b.amount, currency: a.currency };
}

/** Subtrahiert b von a (gleiche Währung). */
export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount - b.amount, currency: a.currency };
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new Error(`Währungen unterscheiden sich: ${a.currency} vs. ${b.currency}`);
  }
}

/**
 * Serialisierung für die API: bigint ist nicht JSON-fähig, daher als String.
 * Format: { amount: "<mikro-einheiten als string>", currency }.
 */
export const moneySchema = z.object({
  amount: z.string().regex(/^-?\d+$/, 'amount muss eine ganzzahlige Mikro-Einheit als String sein'),
  currency: z.enum(['EUR', 'USD', 'CHF']),
});

export type MoneyDto = z.infer<typeof moneySchema>;

export function toMoneyDto(value: Money): MoneyDto {
  return { amount: value.amount.toString(), currency: value.currency };
}

export function fromMoneyDto(dto: MoneyDto): Money {
  return { amount: BigInt(dto.amount), currency: dto.currency };
}
